/**
 * TOTP enrolment helpers (C30, owner feedback O-11, D-282).
 *
 * The owner's authenticator refused a secret typed by hand: base32 uses only A–Z and 2–7, but the letter O in the
 * mono font read as the digit 0 ("illegal character"). Two fixes, shared by every page that shows a secret (portal
 * login enrolment, portal Settings re-enrolment, console setup):
 *  1. a QR code of the otpauth URI, drawn here as one SVG path (no innerHTML, no data: URL, nothing for the CSP to
 *     allow) from the `qrcode-generator` module matrix — the normal way to add the account;
 *  2. the secret for manual entry in groups of four, digits drawn in another colour, with a line saying the key has
 *     no 0, 1, 8 or 9 — so an O can never be typed as a zero.
 * New UI (the old app had no second factor): the frame follows the surrounding card.
 */
import qrcode from "qrcode-generator";
import { useMemo } from "react";

/** SVG path data for the QR modules of `text` (error correction L — a screen does not get dirty — so the smallest version fits). */
export function qrPath(text: string): { readonly size: number; readonly d: string } {
  const qr = qrcode(0, "L");
  qr.addData(text, "Byte");
  qr.make();
  const n = qr.getModuleCount();
  let d = "";
  for (let r = 0; r < n; r++) {
    let c = 0;
    while (c < n) {
      if (!qr.isDark(r, c)) { c++; continue; }
      let e = c;
      while (e < n && qr.isDark(r, e)) e++;
      d += `M${c} ${r}h${e - c}v1h${c - e}z`;
      c = e;
    }
  }
  return { size: n, d };
}

/** Splits a base32 secret into groups of four for reading. */
export function groupSecret(secret: string): string[] {
  return secret.replace(/\s+/g, "").toUpperCase().match(/.{1,4}/g) ?? [];
}

export interface TotpQrProps {
  readonly uri: string;
  /** Wanted width / height in CSS pixels; rounded so that every module is a whole number of pixels (≥ 4). */
  readonly px?: number;
  readonly label: string;
  readonly testId?: string;
}

/** The QR code on a white tile (authenticator cameras need dark-on-light, also in dark mode). */
export function TotpQr({ uri, px = 232, label, testId = "totp-qr" }: TotpQrProps) {
  const { size, d } = useMemo(() => qrPath(uri), [uri]);
  const quiet = 4;
  const box = size + quiet * 2;
  // whole pixels per module: a fractional module blurs at the edges and some phone cameras then miss it
  const side = Math.max(4, Math.round(px / box)) * box;
  return (
    <div className="flex justify-center">
      <div className="bg-white p-3 rounded-2xl shadow-inner border-2 border-slate-100 dark:border-[#30363d]">
        <svg
          data-testid={testId}
          role="img"
          aria-label={label}
          width={side}
          height={side}
          viewBox={`${-quiet} ${-quiet} ${box} ${box}`}
          shapeRendering="crispEdges"
        >
          <rect x={-quiet} y={-quiet} width={box} height={box} fill="#ffffff" />
          <path d={d} fill="#000000" />
        </svg>
      </div>
    </div>
  );
}

export interface TotpSecretTextProps {
  readonly secret: string;
  readonly className?: string;
  readonly digitClassName?: string;
  readonly testId?: string;
}

/** The secret in groups of four; digits (2–7) in another colour so letters and digits never look alike. */
export function TotpSecretText({ secret, className = "", digitClassName = "text-primary-600 dark:text-primary-400", testId }: TotpSecretTextProps) {
  const groups = groupSecret(secret);
  return (
    <div dir="ltr" data-testid={testId} data-secret={secret} className={className}>
      {groups.map((g, i) => (
        <span key={i}>
          {/* the gap is a margin, not a character: a copied key has no separators (some authenticators refuse them) */}
          <span className="whitespace-nowrap inline-block" style={i > 0 ? { marginInlineStart: "0.6em" } : undefined}>
            {Array.from(g).map((ch, j) => (/[0-9]/.test(ch) ? <span key={j} className={digitClassName}>{ch}</span> : <span key={j}>{ch}</span>))}
          </span>
        </span>
      ))}
    </div>
  );
}
